import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { StoreState, UpdaterFunction } from './Store';
import { StoreDefinition, StoresContext } from './StoreContext';

export interface UseStoreResult<T> {
  /** The data currently stored locally */
  data: T | undefined;

  /** Update the local data (and trigger a push to the data source) */
  setData: (updater: UpdaterFunction<T>) => void;

  /** An inferred state of what the store is doing. This is mainly useful
   * for visual feedback to the user. */
  state: StoreState;

  /** Force a download of the data from the data source. If you know the
   * data has changed, call this!
   */
  download: () => Promise<void>;

  /** If there was an error, it will be stored here. This can be both upload
   * or download errors
   */
  error: Error | undefined;

  /** Wait until the store is idle. This is useful for displaying state to the user. */
  waitForSync: () => Promise<void>;

  undo: () => void;
  redo: () => void;
}

/**
 * Finds or creates a store in the StoresContext and subscribes to changes to it.
 *
 * @param storeDefintion How to idenfity and create a store.
 * @returns
 */
export const useStore = <T>(storeDefintion: StoreDefinition<T>): UseStoreResult<T> => {
  const { getOrCreateStore } = useContext(StoresContext);

  const store = useMemo(() => {
    return getOrCreateStore(storeDefintion);
  }, [storeDefintion, getOrCreateStore]);
  const [state, setState] = useState<StoreState>('idle');
  const [error, setError] = useState<Error | undefined>(undefined);
  const [data, setData] = useState<T>();

  useEffect(
    () =>
      store.subscribe((p, state, error) => {
        setData(p);
        setState(state);
        setError(error);
      }),
    [setData, state, store],
  );

  const download = useCallback(async () => {
    // This is needed to avoid debinding `this` which would happen if we passed
    // `store.download` directly out of the hook.
    await store.download();
  }, [store]);

  const updater = useCallback(
    (updater: UpdaterFunction<T>) => {
      store.setData(updater);
    },
    [store],
  );

  const waitForSync = useCallback(async () => {
    if (state === 'idle' && !store.isDirty()) {
      return;
    }
    await new Promise((resolve, reject) => {
      const unsubscribe = store.subscribe((_data, state, error) => {
        if (state === 'idle' && !store.isDirty()) {
          unsubscribe();
          resolve(undefined);
        }
        if (state === 'error') {
          unsubscribe();
          reject(error);
        }
      });
    });
  }, [state, store]);

  return {
    data,
    setData: updater,
    state,
    download,
    error,
    waitForSync,
    undo: () => store.undo(),
    redo: () => store.redo(),
  };
};
